import { transport, BASE_PATH } from "@/src/config/apiConfig.js";
import { APP_STORE_BASE_URL, APP_STORE_API_KEY } from "@/src/utils/appStoreConfig.js";

const appStoreTransport = {
  async get(url, config = {}) {
    const workspaceId = localStorage.getItem("currentWorkspaceId");
    const userId = config.userId || localStorage.getItem("userId");

    const urlWithParams = url.includes("?")
      ? `${url}&userId=${userId}`
      : `${url}?userId=${userId}`;

    const response = await fetch(`${APP_STORE_BASE_URL}${urlWithParams}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${APP_STORE_API_KEY}`,
        "X-Workspace-Id": workspaceId,
        "X-User-Id": userId,
        ...config.headers,
      },
      ...config,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return { data: await response.json() };
  },

  async post(url, data, config = {}) {
    const workspaceId = localStorage.getItem("currentWorkspaceId");
    const userId = config.userId || localStorage.getItem("userId");

    const urlWithParams = url.includes("?")
      ? `${url}&userId=${userId}`
      : `${url}?userId=${userId}`;

    const response = await fetch(`${APP_STORE_BASE_URL}${urlWithParams}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${APP_STORE_API_KEY}`,
        "X-Workspace-Id": workspaceId,
        "X-User-Id": userId,
        ...config.headers,
      },
      body: JSON.stringify(data),
      ...config,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return { data: await response.json() };
  },

  async delete(url, config = {}) {
    const workspaceId = localStorage.getItem("currentWorkspaceId");
    const userId = config.userId || localStorage.getItem("userId");

    const urlWithParams = url.includes("?")
      ? `${url}&userId=${userId}`
      : `${url}?userId=${userId}`;

    const response = await fetch(`${APP_STORE_BASE_URL}${urlWithParams}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${APP_STORE_API_KEY}`,
        "X-Workspace-Id": workspaceId,
        "X-User-Id": userId,
        ...config.headers,
      },
      ...config,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return { data: await response.json() };
  },
};

export default {
  async getCategorizedApps(billingVersion = "v1") {
    try {
      const res = await appStoreTransport.get(
        `/api/v1/apps/categorized?billingVersion=${billingVersion}`,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getCategorizedApps: ",
        error,
      );
      throw error;
    }
  },

  async getAppById(appId) {
    try {
      const basePath = `${BASE_PATH}/api/apps/${appId}/details`;
      const userId = localStorage.getItem("userId");
      const res = await transport.get(basePath, {
        params: { user_id: userId },
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getAppById: ", error);
      throw error;
    }
  },

  async getAppByIdPublic(appId) {
    try {
      const res = await appStoreTransport.get(`/api/v1/apps/${appId}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getAppByIdPublic: ",
        error,
      );
      throw error;
    }
  },

  async getAppBySlug(slug) {
    try {
      const res = await appStoreTransport.get(`/api/v1/apps/by-slug?slug=${encodeURIComponent(slug)}`);
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getAppBySlug: ",
        error,
      );
      throw error;
    }
  },

  async getInstalledApps() {
    try {
      const res = await appStoreTransport.get("/api/v1/apps/installed");
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getInstalledApps: ",
        error,
      );
      throw error;
    }
  },

  async installApp(appId, planVersionId, billingVersion = "v1") {
    try {
      const res = await appStoreTransport.post(
        "/api/v1/apps/install?billingVersion=" + billingVersion,
        {
          app_id: appId,
          plan_id: planVersionId,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing installApp: ", error);
      throw error;
    }
  },

  async uninstallApp(appId, userId = null, billingVersion = "v1") {
    try {
      const config = userId ? { userId: userId } : {};
      const res = await appStoreTransport.delete(
        `/api/v1/apps/installed/${appId}?billingVersion=${billingVersion}`,
        config,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing uninstallApp: ", error);
      throw error;
    }
  },

  async bulkUninstallApps(appIdOrIds, userIds, billingVersion = "v1") {
    try {
      // Calls Rails backend which proxies to App Store bulk-uninstall
      const payload = Array.isArray(appIdOrIds)
        ? { app_ids: appIdOrIds, user_ids: userIds }
        : { app_id: appIdOrIds, user_ids: userIds };
      const res = await transport.post(
        `${BASE_PATH}/api/apps/bulk-uninstall?billingVersion=${billingVersion}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing bulkUninstallApps: ",
        error,
      );
      throw error;
    }
  },

  async bulkInstallApp(
    appIdOrIds,
    userIds,
    teamIds,
    planVersionId,
    billingVersion = "v1",
  ) {
    try {
      // Call Rails backend which will extract users from teams and call app store service
      const payload = Array.isArray(appIdOrIds)
        ? {
            app_ids: appIdOrIds,
            user_ids: userIds,
            team_ids: teamIds,
            plan_id: planVersionId,
          }
        : {
            app_id: appIdOrIds,
            user_ids: userIds,
            team_ids: teamIds,
            plan_id: planVersionId,
          };
      const res = await transport.post(
        `${BASE_PATH}/api/apps/bulk-install?billingVersion=${billingVersion}`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing bulkInstallApp: ",
        error,
      );
      throw error;
    }
  },

  async getSidebarSections() {
    try {
      const res = await appStoreTransport.get("/api/v1/apps/sidebar-schema");
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getSidebarSections: ",
        error,
      );
      throw error;
    }
  },

  async getFirstAppRoute(appId) {
    try {
      const res = await appStoreTransport.post("/api/v1/apps/first-route", {
        app_id: appId,
      });
      return res.data;
    } catch (error) {
      console.error(
        "An error occurred while executing getFirstAppRoute: ",
        error,
      );
      throw error;
    }
  },

  // These methods still use the Rails backend for workspace configuration

  async toggleAndRefreshAppStoreVersion() {
    try {
      const toggleRes = await transport.post(
        `${BASE_PATH}/api/apps/toggle-version`,
      );

      if (toggleRes.data?.success) {
        const refreshRes = await transport.get(
          `${BASE_PATH}/api/workspace_app_store_version`,
        );
        return {
          ...toggleRes.data,
          refreshed_version: refreshRes.data,
        };
      }

      return toggleRes.data;
    } catch (error) {
      console.error(
        "An error occurred while executing toggleAndRefreshAppStoreVersion: ",
        error,
      );
      throw error;
    }
  },

  async getUsersByIds(userIds) {
    try {
      const res = await transport.post(`${BASE_PATH}/api/apps/users/details`, {
        user_ids: userIds,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while executing getUsersByIds: ", error);
      throw error;
    }
  },

  async getWorkspaceConfigurationByKey(workspaceId, key) {
    try {
      const res = await transport.get(
        `${BASE_PATH}/api/workspace_configurations/${workspaceId}/${key}`,
      );
      return res.data;
    } catch (error) {
      console.error("Error fetching workspace configuration:", error);
      return null;
    }
  },

  async updateDefaultCountryCode(value) {
    try {
      const res = await transport.post(
        `${BASE_PATH}/api/workspace_configurations/default_country_code`,
        { value },
      );
      return res.data;
    } catch (error) {
      console.error("Error updating default country code:", error);
      throw error;
    }
  },

  async forkApp(appId) {
    try {
      const res = await appStoreTransport.post(`/api/v1/apps/${appId}/fork`, {});
      return res.data;
    } catch (error) {
      console.error("An error occurred while forking app:", error);
      throw error;
    }
  },
};
